import React from 'react';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import cookie from 'cookie';
import Head from 'next/head';
import API from 'utils/ServerSideAPI';
import { UserProfileScreen } from 'screens/Profile/UserProfileScreen';
import { BootstrapLoadingWrapper } from 'components/BootstrapLoadingWrapper';
import { UserNotFoundScreen } from 'screens/Profile/components/UserNotFoundScreen';
import { MIN_PROFILE_PREVIEW_WORKOUTS, ProfileLinkPreviewMetadataResponse } from 'hevy-shared';
import { t } from 'translations/t';
import { formatNumber } from 'translations/formatters';

import { BrowserLanguageProps } from 'utils/types';
import { browserLanguageFromContext } from 'utils/pageUtils';

type UserProfileProps = {
  username: string | null;
  metadata: ProfileLinkPreviewMetadataResponse | null;
  isAuthenticated: boolean;
} & BrowserLanguageProps;

export const getServerSideProps: GetServerSideProps = async (
  context,
): Promise<GetServerSidePropsResult<UserProfileProps>> => {
  const { username } = context.query;
  const rawCookies = context.req?.headers.cookie;
  const cookies = rawCookies ? cookie.parse(rawCookies) : {};

  const isAuthenticated = API.isAuthenticated(cookies);

  if (typeof username !== 'string') {
    return {
      props: {
        username: null,
        metadata: null,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  }

  try {
    const result = await API.getUserPreviewMetadata(username);
    return {
      props: {
        username,
        metadata: result.data,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  } catch {
    return {
      props: {
        username,
        metadata: null,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  }
};

const previewDescription = ({ workout_count, joined_year }: ProfileLinkPreviewMetadataResponse) => {
  const since = t('web.metadata.user.loggingSince', { year: joined_year });
  return workout_count < MIN_PROFILE_PREVIEW_WORKOUTS
    ? since
    : `${t('web.metadata.user.workouts', { count: formatNumber(workout_count) })} · ${since}`;
};

export default function UserProfile({ username, metadata, browserLanguage }: UserProfileProps) {
  return (
    <>
      {metadata && (
        <Head>
          {/* using metadata.username because it isn't nullable, unlike username */}
          <title>{t('web.metadata.user.desc', { username: metadata.username })}</title>
          <meta
            name="description"
            content={t('web.metadata.user.desc', { username: metadata.username })}
          />
          <meta
            name="robots"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
          <link rel="canonical" href={metadata.url} />
          <meta property="og:locale" content={browserLanguage} />
          <meta property="og:locale:alternate" content="ar" />
          <meta property="og:locale:alternate" content="ca" />
          <meta property="og:locale:alternate" content="de" />
          <meta property="og:locale:alternate" content="en" />
          <meta property="og:locale:alternate" content="es" />
          <meta property="og:locale:alternate" content="fr" />
          <meta property="og:locale:alternate" content="hi" />
          <meta property="og:locale:alternate" content="it" />
          <meta property="og:locale:alternate" content="ja" />
          <meta property="og:locale:alternate" content="ko" />
          <meta property="og:locale:alternate" content="pl" />
          <meta property="og:locale:alternate" content="pt_BR" />
          <meta property="og:locale:alternate" content="pt_PT" />
          <meta property="og:locale:alternate" content="ru" />
          <meta property="og:locale:alternate" content="tr" />
          <meta property="og:locale:alternate" content="zh_CN" />
          <meta property="og:locale:alternate" content="zh_TW" />
          <meta name="og:type" content="website" />
          <meta
            property="og:title"
            content={t('web.metadata.user.title', { username: metadata.username })}
          />
          <meta property="og:description" content={previewDescription(metadata)} />
          <meta property="og:url" content={metadata.url} />
          <meta property="og:site_name" content={t('web.metadata.socialWorkoutTracker')} />
          <meta property="og:image" content={metadata.image_url} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:creator" content="@hevyapp" />
          <meta name="twitter:site" content="@hevyapp" />
          <meta
            name="twitter:title"
            content={t('web.metadata.user.title', { username: metadata.username })}
          />
          <meta name="twitter:description" content={previewDescription(metadata)} />
          <meta name="twitter:image" content={metadata.twitter_image_url} />
          <meta name="twitter:label1" content={t('web.metadata.user')} />
          <meta name="twitter:data1" content={metadata.username} />
        </Head>
      )}
      <BootstrapLoadingWrapper>
        {username ? <UserProfileScreen username={username} /> : <UserNotFoundScreen />}
      </BootstrapLoadingWrapper>
    </>
  );
}
